import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useSettingsStore, useSettingsHydrated } from '@/stores/settings-store';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import { getRegionName, getCountyCodeForRegion } from '@/data/regions';
import { apiGet } from '@/services/api-client';
import OfficialSourcesNotice from '@/components/OfficialSourcesNotice';

// Kolla om vi är i dev- eller staging-läge (test-toggle ska INTE synas i prod)
const appEnv = import.meta.env.VITE_APP_ENV || 'development';
const isNonProd = !import.meta.env.PROD && (import.meta.env.DEV || appEnv === 'staging' || appEnv === 'development');
import { 
  AlertTriangle, 
  CheckCircle, 
  Radio, 
  MapPin, 
  Clock, 
  Info,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Settings,
} from 'lucide-react';

interface VmaAlert {
  id: string;
  headline: string;
  description: string;
  severity: 'extreme' | 'severe' | 'moderate' | 'minor';
  sent: string;
  areas: { areaDesc: string }[];
  instruction?: string;
  isNational?: boolean;
  isActive?: boolean;
  lastSeenAt?: string;
}

interface VmaResponse {
  alerts: VmaAlert[];
  meta: {
    count: number;
    activeCount?: number;
    recentCount?: number;
    counties: string[];
    includeRecent?: boolean;
    historyHours?: number;
    fetchedAt: string;
  };
}

export default function VmaPage() {
  const { t, i18n } = useTranslation();
  const { vmaRegion, fetchFromServer } = useSettingsStore();
  const { isAuthenticated } = useAuthStore();
  const authHydrated = useAuthHydrated();
  const settingsHydrated = useSettingsHydrated();
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  
  // Testdata toggle - sparas i localStorage (endast i dev/staging, aldrig prod)
  const [useTestData, setUseTestData] = useState(() => {
    if (!isNonProd) return false;
    const saved = localStorage.getItem('vma-use-test-data');
    return saved !== null ? saved === 'true' : true; // Default true i dev/staging
  });
  
  // Spara testdata-toggle till localStorage och notifiera Layout
  const handleTestDataToggle = (value: boolean) => {
    setUseTestData(value);
    localStorage.setItem('vma-use-test-data', String(value));
    // Skicka custom event så Layout uppdaterar sin ikon
    window.dispatchEvent(new CustomEvent('vma-test-toggle'));
  };

  const regionName = vmaRegion ? getRegionName(vmaRegion) : 'Hela Sverige';

  // Hämta inställningar från server vid mount (vänta på BÅDA hydrations)
  useEffect(() => {
    if (authHydrated && settingsHydrated && isAuthenticated) {
      fetchFromServer();
    }
  }, [authHydrated, settingsHydrated, isAuthenticated, fetchFromServer]);

  const { data, isLoading, error, refetch, isFetching } = useQuery<VmaResponse>({
    queryKey: ['vma-alerts', vmaRegion, useTestData],
    queryFn: async () => {
      const base = useTestData ? '/vma/alerts?test=true' : '/vma/alerts?includeRecent=true&historyHours=24';
      const sep = base.includes('?') ? '&' : '?';
      // Map region -> county code and prefer sending `counties` so backend uses Krisinformation filter directly
      const countyCode = vmaRegion ? getCountyCodeForRegion(vmaRegion) : undefined;
      const regionPart = countyCode ? `${sep}counties=${encodeURIComponent(countyCode)}` : (vmaRegion ? `${sep}region=${encodeURIComponent(vmaRegion)}` : '');
      const url = `${base}${regionPart}`;
      const result = await apiGet<VmaResponse>(url);
      if (result.success && result.data) {
        return result.data;
      }
      throw new Error('Failed to fetch alerts');
    },
    refetchInterval: 5 * 60 * 1000, // Uppdatera var 5:e minut
    staleTime: 2 * 60 * 1000, // Data är "fresh" i 2 minuter
  });

  // Uppdatera VMA-larm när regionen ändras
  useEffect(() => {
    if (settingsHydrated) {
      refetch();
    }
  }, [vmaRegion, settingsHydrated, refetch]);

  const alerts = data?.alerts || [];
  const activeCount = data?.meta.activeCount ?? alerts.filter((alert) => alert.isActive !== false).length;
  const recentCount = data?.meta.recentCount ?? Math.max(0, alerts.filter((alert) => alert.isActive === false).length);

  const severityConfig = {
    extreme: {
      bg: 'bg-red-700/10',
      border: 'border-red-600',
      text: 'text-red-400',
      label: 'Extrem fara',
      icon: AlertTriangle,
    },
    severe: {
      bg: 'bg-red-700/10',
      border: 'border-red-600',
      text: 'text-red-400',
      label: 'Allvarlig fara',
      icon: AlertTriangle,
    },
    moderate: {
      bg: 'bg-red-700/10',
      border: 'border-red-600',
      text: 'text-red-400',
      label: 'Måttlig fara',
      icon: AlertTriangle,
    },
    minor: {
      bg: 'bg-red-700/10',
      border: 'border-red-600',
      text: 'text-red-400',
      label: 'Mindre fara',
      icon: AlertTriangle,
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-orange-500" />
            {t('vma.title')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('vma.subtitle')}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Beredskapshubben är inte en myndighet. Följ alltid officiella instruktioner från ansvariga svenska myndigheter.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-2 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
          title={t('common.refresh')}
        >
          <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Region indicator */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-medium">{t('vma.region.title')}</h3>
              <p className="text-sm text-muted-foreground">{regionName}</p>
            </div>
          </div>
          <Link
            to="/app/settings#vma-region"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-primary hover:bg-primary/10 transition-colors"
          >
            <Settings className="h-4 w-4" />
{t('vma.region.change')}
          </Link>
        </div>
      </div>

      {/* Dev/Staging: Test/Prod toggle - endast synlig i dev/staging, aldrig i prod */}
      {isNonProd && (
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-yellow-500" />
              <div>
                <h3 className="font-medium text-yellow-500">Dev: Testdata</h3>
                <p className="text-sm text-muted-foreground">
                  {useTestData ? 'Visar testmeddelanden från Krisinformation.se' : 'Visar riktiga VMA (kan vara tomt)'}
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={useTestData}
              onClick={() => handleTestDataToggle(!useTestData)}
              className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${
                useTestData ? 'bg-yellow-500' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  useTestData ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-8 text-muted-foreground">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
          {t('common.loading')}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-400 font-medium">{t('common.error')}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {t('vma.error.checkConnection')}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* No alerts */}
      {alerts && alerts.length === 0 && !isLoading && (
        <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-8 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
          <h2 className="mt-4 text-xl font-semibold text-green-400">
            {t('vma.noAlerts')}
          </h2>
          <p className="mt-2 text-muted-foreground">
            {t('vma.noAlertsDesc')}
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            {t('vma.lastChecked')}: {new Date().toLocaleString(i18n.language === 'sv' ? 'sv-SE' : 'en-GB')}
          </p>
        </div>
      )}

      {/* Alerts list */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {recentCount > 0
              ? `Aktiva VMA (${activeCount}) och senaste 24 timmarna (${recentCount})`
              : `Aktiva varningar (${activeCount})`}
          </h2>
          {alerts.map((alert) => {
            const config = severityConfig[alert.severity];
            const Icon = config.icon;
            const isExpanded = expandedAlert === alert.id;
            const isActive = alert.isActive !== false;

            return (
              <div
                  key={alert.id}
                  className={`rounded-xl border ${config.border} ${config.bg} overflow-hidden`}
                >
                <button
                  onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start gap-4">
                    <Icon className={`h-6 w-6 ${config.text} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold">{alert.headline}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.text} ${config.bg} border ${config.border}`}>
                            {config.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${isActive ? 'border-green-500/40 bg-green-500/10 text-green-400' : 'border-amber-500/40 bg-amber-500/10 text-amber-400'}`}>
                            {isActive ? 'Aktiv' : 'Senaste 24h'}
                          </span>
                        </div>
                      </div>
                      
                      <p className={`mt-2 text-sm ${isExpanded ? '' : 'line-clamp-2'}`}>
                        {alert.description}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {new Date(alert.sent).toLocaleString('sv-SE')}
                        </span>
                        {!isActive && alert.lastSeenAt && (
                          <span className="flex items-center gap-1">
                            <Info className="h-4 w-4" />
                            Senast sedd {new Date(alert.lastSeenAt).toLocaleString('sv-SE')}
                          </span>
                        )}
                        {alert.areas.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {alert.areas.map(a => a.areaDesc).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                  </div>
                </button>

                {isExpanded && alert.instruction && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="border-t border-border/50 pt-4">
                      <h4 className="font-medium mb-2">Instruktioner</h4>
                      <p className="text-sm text-muted-foreground">{alert.instruction}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info om VMA */}
      <div className="rounded-xl bg-orange-500/10 border border-orange-500/20 p-6">
        <div className="flex items-start gap-4">
          <Radio className="h-6 w-6 text-orange-400 shrink-0" />
          <div>
            <h3 className="font-semibold text-orange-400">{t('vma.info.title')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('vma.info.description')} <strong>{t('vma.info.signal')}</strong>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>{t('vma.info.actions')}</strong>
            </p>
          </div>
        </div>
      </div>

      <OfficialSourcesNotice
        title="Officiella källor för VMA och krisinformation"
        description="VMA i appen visas som vägledande information baserad på officiella källor. Vid skarpa händelser ska du alltid följa instruktioner från ansvariga myndigheter och Sveriges Radio P4."
        extraLinks={[
          {
            label: 'Sveriges Radio P4',
            href: 'https://sverigesradio.se/p4',
            description: 'Beredskapskanal för viktiga meddelanden och löpande information.',
          },
        ]}
      />
    </div>
  );
}
